import { useState } from 'react';
import Label from '../../../Label/Label';
import Tooltip from '../../../ToolTip/ToolTip';
import { Dropzone } from '../../../Dropzone/Dropzone';
import { Input } from '../../../Input';
import { FormStatus } from './formStatus';
import { useClaim } from '../../../../hooks/useClaim';

/**
 * TODO:
 * 1. Error handling
 *   - Dropzone issues
 *   - Logic issues where you upload a file that is fine, but try a second file that isn't and it just ignores the second but not the first
 *   - Management API issues
 *   - Cloudfront issues
 *      - Scenario: both Management API calls succeed (signed-url and submission), but the upload fails to S3 for whatever reason
 * 2. Success state
 *   - Hide form and display success
 * 3. Fix console issue with DropZone and this component
 * 4. Refactor and split up hooks and this component
 * 5. Allow scenario where the user doesn't upload files
 *
 */
const ClaimForm: React.FC = () => {
  const [dropZoneErrorMessage] = useState<string | null>(null);
  const { formState, setFormState, handleSubmit } = useClaim();

  return (
    <>
      {import.meta.env.VITE_ENVIRONMENT === 'local' ? (
        <div className="bg-red-100 my-4 p-4">
          <pre>{JSON.stringify(formState)}</pre>
          <pre>{JSON.stringify(dropZoneErrorMessage)}</pre>
        </div>
      ) : null}
      <FormStatus state={formState} />

      {!formState.message || (formState.message && !formState.isSuccessful) ? (
        <form id="claim-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col">
              <h2 className="font-bold my-6">Applicant Details</h2>
              <Label fieldName="email" text="Email" isRequired={true}>
                <Tooltip content="The email must be primary email that was used to purchase the insurance policy">
                  <span className="inline-block bg-inglelewer-pink rounded-full text-center text-white text-sm leading-5 w-5 h-5 hover:cursor-pointer ml-1">
                    ?
                  </span>
                </Tooltip>
                <Input
                  inputData={formState}
                  name={'email'}
                  type={'email'}
                  onChangeHandler={setFormState}
                  placeholder={'studentemail@email.com'}
                  classes="mt-0 block w-full px-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-inglelewer-pink"
                  isRequired
                />
              </Label>
            </div>
            <div className="flex flex-col">
              <h2 className="font-bold my-6">Policy Details</h2>
              <Label
                fieldName="policyNumber"
                text="Policy Number"
                isRequired={true}
              >
                <Tooltip content="Policy number can be found in the policy confirmation email">
                  <span className="inline-block bg-inglelewer-pink rounded-full text-center text-white text-sm leading-5 w-5 h-5 hover:cursor-pointer ml-1">
                    ?
                  </span>
                </Tooltip>
                <Input
                  inputData={formState}
                  name={'policyNumber'}
                  onChangeHandler={setFormState}
                  classes="mt-0 block w-full px-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-inglelewer-pink"
                  isRequired
                />
              </Label>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 my-4">
            <h2 className="font-bold my-6">
              Medical Records{' '}
              <span className="text-red-400 font-bold text-lg">*</span>
            </h2>
            <Dropzone
              errorMessageProp={dropZoneErrorMessage}
              inputData={formState}
              onChangeHandler={setFormState}
              isRequired
            />

            <div className="flex flex-wrap items-start mb-4">
              <input
                id="checkbox"
                type="checkbox"
                value=""
                className="w-4 h-4 mt-2 text-inglelewer-pink border-gray-300 rounded focus:ring-inglelewer-pink focus:ring-2"
                required
              />
              <label
                htmlFor="checkbox"
                className="ms-2 text-sm font-medium text-gray-700"
              >
                <span className="text-red-400 font-bold text-lg">* </span>
                Please read carefully the statement on authorization and certify
                that the claim is accurate.
              </label>
              <Tooltip
                content="I authorize any doctor, medical practitioner, hospital, facility
                                providing medical or health-related services, third-party
                                administrator, provincial plan, and any other insurer to release and
                                exchange with Lloyd’s, Ingle Lewer, Lewer Canada Ltd, and Sutton Special Risk or any of their
                                representatives, any information (including personal health data and
                                records) required to process this claim. I authorize any third party
                                providing me with assistance in this claim process to have access to
                                any and all relevant claims information related to the adjudication
                                of my claim with Lloyd’s, Ingle Lewer, Lewer Canada Ltd, and Sutton Special Risk. I authorize Ingle Lewer, Lewer
                                Canada Ltd, and Sutton Special Risk to
                                coordinate the payment of benefits with any insurance carriers that
                                may have a liability for this claim and assign to Lloyd’s,
                                Ingle Lewer, Lewer Canada Ltd, and  Sutton Special Risk any benefits payable from any other sources for
                                losses covered under this policy, and authorize and direct such
                                payers to forward payment directly to Lloyd’s, Ingle Lewer, Lewer
                                Canada Ltd, and/or Sutton Special Risk I
                                confirm below by my signature that I am authorized to act on behalf
                                of any of my dependants for these purposes. A photocopy of this
                                authorization shall be as valid as the original. I authorize
                                Ingle Lewer, Lewer Canada Ltd, and  Sutton Special Risk to coordinate the payment of benefits with any
                                insurance carriers that may have a liability for this claim and
                                assign to Lloyd’s, Ingle Lewer, Lewer Canada Ltd, and Sutton Special Risk any benefits payable from any other
                                sources for losses covered under this policy, and authorize and
                                direct such payers to forward payment directly to Lloyd’s,
                                Ingle Lewer, Lewer Canada Ltd, and/or Sutton Special Risk. I certify that the information provided in
                                connection with this claim is complete, true, and accurate."
              >
                <span className="inline-block bg-inglelewer-pink rounded-full text-center text-white text-sm leading-5 w-5 h-5 hover:cursor-pointer ml-1">
                  ?
                </span>
              </Tooltip>
            </div>
            <div className="flex justify-center clear">
              <button
                // onClick={handleSubmit}
                // disabled={isSubmitting}
                className="text-white bg-inglelewer-purple hover:bg-inglelewer-pink focus:ring-4 focus:ring-blue-300 font-medium rounded-lg w-1/4 px-16 py-2.5 cursor-pointer"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      ) : null}
    </>
  );
};

export default ClaimForm;
